import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  CheckCircle,
  User,
  Search,
  ChevronDown,
  Plus,
  Trash2,
  Pencil
} from 'lucide-react';
import MainService from '../../services/MainService';

// Searchable Dropdown Component
const SearchableSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select...', 
  disabled = false,
  required = false,
  label,
  searchPlaceholder = 'Search...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.Id === value);
  const displayValue = selectedOption ? (selectedOption.Name || selectedOption.Number || '') : '';

  const filteredOptions = options.filter(opt => {
    const searchText = (opt.Name || opt.Number || '').toLowerCase();
    return searchText.includes(searchTerm.toLowerCase());
  });

  const handleSelect = (option) => {
    onChange(option.Id);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent text-left flex items-center justify-between ${
            disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
        >
          <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
            {displayValue || placeholder}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border-0 border-b-2 border-gray-200 focus:outline-none focus:border-[#46955A] bg-transparent text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.Id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full px-4 py-2 text-left hover:bg-[#f0f9f4] transition-colors ${
                      value === option.Id ? 'bg-[#f0f9f4] text-[#46955A] font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option.Name || option.Number}
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Location Autocomplete Component
const LocationAutocomplete = ({ 
  value, 
  onChange, 
  emirateId, 
  placeholder = 'Search location...',
  disabled = false,
  required = false,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2 && emirateId) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          const response = await MainService.LocationsAutoCompleteByEmirate({
            Keyword: searchTerm,
            EmirateId: emirateId
          });
          
          const list = Array.isArray(response)
            ? response
            : response?.Data ?? response?.data ?? [];
          setLocations(Array.isArray(list) ? list : []);
        } catch (err) {
          console.error('Error loading locations:', err);
          setLocations([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    } else {
      setLocations([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, emirateId]);

  const handleSelect = (location) => {
    onChange(location);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <input
          type="text"
          value={value?.Name || searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (emirateId && searchTerm.length >= 2) {
              setIsOpen(true);
            }
          }}
          disabled={disabled || !emirateId}
          placeholder={disabled || !emirateId ? 'Select emirate first' : placeholder}
          className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${
            disabled || !emirateId ? 'bg-gray-50 cursor-not-allowed opacity-60' : ''
          }`}
        />
        
        {isOpen && emirateId && searchTerm.length >= 2 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="max-h-60 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">Loading...</div>
              ) : locations.length > 0 ? (
                locations.map((location) => (
                  <button
                    key={location.Id}
                    type="button"
                    onClick={() => handleSelect(location)}
                    className="w-full px-4 py-2 text-left hover:bg-[#f0f9f4] transition-colors text-gray-700"
                  >
                    {location.Name}
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  No locations found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const createEmptyCustomerAddressDraft = () => ({
  POBOX: '',
  Address: '',
  IsDefault: false,
  IsActive: true,
  LocationId: '',
  EmirateId: '',
  Location: { Name: '' },
  LocationUrl: '',
});

const AddCustomer = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [emirates, setEmirates] = useState([]);
  const [addressDraft, setAddressDraft] = useState(() => createEmptyCustomerAddressDraft());
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);

  // Customer form data
  const [customer, setCustomer] = useState({
    Id: 0,
    Name: '',
    MobileNo1: '',
    MobileNo2: '',
    TelNo: '',
    EmailId: '',
    Remarks: '',
    Date: new Date().toISOString().split('T')[0],
    CustomerAddresses: []
  });

  useEffect(() => {
    const initialize = async () => {
      await loadDropdowns();
      if (customerId && parseInt(customerId) > 0) {
        await loadCustomer(parseInt(customerId));
      }
    };
    initialize();
  }, [customerId]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadDropdowns = async () => {
    try {
      const emiratesRes = await MainService.EmiratesInDropDown();
      setEmirates(emiratesRes || []);
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Failed to load dropdown data');
    }
  };

  const loadCustomer = async (id) => {
    try {
      setLoading(true);
      const response = await MainService.CustomerById(id);
      if (response) {
        setCustomer({
          ...response,
          Date: response.Date ? new Date(response.Date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          CustomerAddresses: response.CustomerAddresses || []
        });
        setAddressDraft(createEmptyCustomerAddressDraft());
        setEditingAddressIndex(null);
      }
    } catch (err) {
      console.error('Error loading customer:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCustomer(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const ensureAtLeastOneDefault = (addresses) => {
    const next = [...addresses];
    const activeIdx = next.map((a, i) => (a.IsActive ? i : -1)).filter((i) => i >= 0);
    if (activeIdx.length === 0) return next;
    if (!activeIdx.some((i) => next[i].IsDefault)) {
      const first = activeIdx[0];
      next[first] = { ...next[first], IsDefault: true };
    }
    return next;
  };

  const commitAddressFromDraft = () => {
    if (!addressDraft.EmirateId || !addressDraft.LocationId || !addressDraft.Address?.trim()) {
      setError('Emirate, Location, and Address are required');
      return;
    }
    setError('');

    if (editingAddressIndex !== null) {
      const idx = editingAddressIndex;
      setCustomer((prev) => {
        if (idx < 0 || idx >= prev.CustomerAddresses.length) return prev;
        const prevRow = prev.CustomerAddresses[idx];
        if (!prevRow?.IsActive) return prev;
        const updated = {
          ...prevRow,
          POBOX: addressDraft.POBOX || '',
          Address: addressDraft.Address || '',
          EmirateId: addressDraft.EmirateId,
          LocationId: addressDraft.LocationId,
          Location: addressDraft.Location?.Name ? { Name: addressDraft.Location.Name } : { Name: '' },
          LocationUrl: addressDraft.LocationUrl || '',
          IsDefault: !!addressDraft.IsDefault,
          IsActive: true,
        };
        let addresses = [...prev.CustomerAddresses];
        if (updated.IsDefault) {
          addresses = addresses.map((a, i) =>
            i === idx ? updated : a.IsActive ? { ...a, IsDefault: false } : a
          );
        } else {
          addresses[idx] = updated;
        }
        addresses = ensureAtLeastOneDefault(addresses);
        return { ...prev, CustomerAddresses: addresses };
      });
      setEditingAddressIndex(null);
      setAddressDraft(createEmptyCustomerAddressDraft());
      return;
    }

    setCustomer((prev) => {
      const activeBefore = prev.CustomerAddresses.filter((a) => a.IsActive);
      const isFirst = activeBefore.length === 0;
      const newRow = {
        ...addressDraft,
        IsActive: true,
        IsDefault: isFirst ? true : !!addressDraft.IsDefault,
      };
      let addresses = [...prev.CustomerAddresses];
      if (newRow.IsDefault) {
        addresses = addresses.map((a) => (a.IsActive ? { ...a, IsDefault: false } : a));
      }
      addresses.push(newRow);
      addresses = ensureAtLeastOneDefault(addresses);
      return { ...prev, CustomerAddresses: addresses };
    });
    setAddressDraft(createEmptyCustomerAddressDraft());
  };

  const startEditAddress = (globalIndex) => {
    const row = customer.CustomerAddresses[globalIndex];
    if (!row?.IsActive) return;
    setAddressDraft({
      POBOX: row.POBOX || '',
      Address: row.Address || '',
      IsDefault: !!row.IsDefault,
      IsActive: true,
      LocationId: row.LocationId || '',
      EmirateId: row.EmirateId || '',
      Location: row.Location?.Name ? { Name: row.Location.Name } : { Name: '' },
      LocationUrl: row.LocationUrl || '',
    });
    setEditingAddressIndex(globalIndex);
    setError('');
  };

  const cancelAddressEdit = () => {
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyCustomerAddressDraft());
  };

  const removeAddress = (index) => {
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyCustomerAddressDraft());
    setCustomer((prev) => {
      const addresses = [...prev.CustomerAddresses];
      addresses[index] = { ...addresses[index], IsActive: false };
      return {
        ...prev,
        CustomerAddresses: ensureAtLeastOneDefault(addresses),
      };
    });
  };

  const setActiveAddressDefault = (globalIndex) => {
    setCustomer((prev) => ({
      ...prev,
      CustomerAddresses: prev.CustomerAddresses.map((a, idx) =>
        !a.IsActive ? a : { ...a, IsDefault: idx === globalIndex }
      ),
    }));
  };

  const getEmirateName = (id) => {
    const e = emirates.find((x) => x.Id === id);
    return e ? e.Name || e.Number || '—' : '—';
  };

  const handleSave = async () => {
    // Validation
    if (!customer.Name || customer.Name.trim() === '') {
      setError('Enter Name');
      return;
    }
    if (!customer.MobileNo1 || customer.MobileNo1.trim() === '') {
      setError('Enter Mobile Number');
      return;
    }

    const activeAddresses = customer.CustomerAddresses.filter(a => a.IsActive);
    if (activeAddresses.length === 0) {
      setError('Add at least one address');
      return;
    }

    let addressInvalid = false;
    let defaultFound = false;
    let defaultCount = 0;

    activeAddresses.forEach(addr => {
      if (addr.IsDefault) {
        defaultFound = true;
        defaultCount++;
      }
      if (!addr.EmirateId || !addr.LocationId || !addr.Address || addr.Address.trim() === '') {
        addressInvalid = true;
      }
    });

    if (addressInvalid) {
      setError('Invalid Address Found | Emirate, Location, Address Compulsory');
      return;
    }

    if (!defaultFound) {
      setError('Make One Address As Default');
      return;
    }

    if (defaultCount !== 1) {
      setError('Make One Address As Default');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.SaveCustomer(customer);
      
      if (response === 'Success' || response === true) {
        setSuccess('Customer saved successfully');
        setTimeout(() => {
          navigate('/home/accounts/customers');
        }, 1500);
      } else {
        setError(response || 'Failed to save customer');
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      setError('Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCustomer({
      Id: 0,
      Name: '',
      MobileNo1: '',
      MobileNo2: '',
      TelNo: '',
      EmailId: '',
      Remarks: '',
      Date: new Date().toISOString().split('T')[0],
      CustomerAddresses: []
    });
    setAddressDraft(createEmptyCustomerAddressDraft());
    setEditingAddressIndex(null);
  };

  const tabs = [
    { id: 0, label: 'Basic' },
    { id: 1, label: 'Address' }
  ];

  const activeAddresses = customer.CustomerAddresses.filter(a => a.IsActive);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-1 border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <User className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">
                {customerId && parseInt(customerId) > 0 ? 'Edit Customer' : 'Add Customer'}
              </h1>
            </div>
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {loading && (
                <div className="w-6 h-6 border-2 border-[#46955A] border-t-transparent rounded-full animate-spin"></div>
              )}
              <button
                onClick={handleReset}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] hover:border-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 ml-8">
            {customerId && parseInt(customerId) > 0 
              ? 'Update customer information and details' 
              : 'Create new customer and manage their information'}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#46955A] border-b-2 border-[#46955A]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Basic Tab */}
        {activeTab === 0 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customer.Name}
                  onChange={(e) => handleInputChange('Name', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Mobile No1 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customer.MobileNo1}
                  onChange={(e) => handleInputChange('MobileNo1', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Mobile No2</label>
                <input
                  type="text"
                  value={customer.MobileNo2}
                  onChange={(e) => handleInputChange('MobileNo2', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Tel No</label>
                <input
                  type="text"
                  value={customer.TelNo}
                  onChange={(e) => handleInputChange('TelNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={customer.EmailId}
                  onChange={(e) => handleInputChange('EmailId', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Remarks</label>
                <textarea
                  rows="3"
                  value={customer.Remarks}
                  onChange={(e) => handleInputChange('Remarks', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Address Tab — form on top (add or edit), table below */}
        {activeTab === 1 && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {editingAddressIndex !== null ? 'Edit address' : 'Add address'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {editingAddressIndex !== null ? (
                  <>
                    Update the fields below, then <span className="font-medium text-gray-700">Save changes</span>. Or{' '}
                    <button type="button" onClick={cancelAddressEdit} className="font-medium text-[#46955A] hover:underline">
                      cancel
                    </button>{' '}
                    to discard edits.
                  </>
                ) : (
                  <>
                    Enter details, then <span className="font-medium text-gray-700">Add to list</span>. Use{' '}
                    <span className="font-medium text-gray-700">Edit</span> on a row below to change an existing address.
                  </>
                )}
              </p>
              <div
                className={`mt-4 space-y-4 rounded-lg border bg-gray-50/40 p-4 md:p-5 ${
                  editingAddressIndex !== null ? 'border-[#46955A] ring-1 ring-[#46955A]/25' : 'border-gray-200'
                }`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <SearchableSelect
                    value={addressDraft.EmirateId}
                    onChange={(value) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        EmirateId: value,
                        LocationId: '',
                        Location: { Name: '' },
                      }))
                    }
                    options={emirates}
                    placeholder="Select Emirate"
                    required
                    label="Emirate"
                  />
                  <LocationAutocomplete
                    value={addressDraft.Location}
                    onChange={(location) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        LocationId: location.Id,
                        Location: { Name: location.Name },
                      }))
                    }
                    emirateId={addressDraft.EmirateId}
                    placeholder="Search location..."
                    disabled={!addressDraft.EmirateId}
                    required
                    label="Location"
                  />
                  <div className="flex flex-col justify-end gap-1 pb-2">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!addressDraft.IsDefault}
                        disabled={editingAddressIndex === null && activeAddresses.length === 0}
                        onChange={(e) => setAddressDraft((prev) => ({ ...prev, IsDefault: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-[#46955A] focus:ring-[#46955A] disabled:opacity-50"
                      />
                      <span className="text-sm font-medium text-gray-700">Set as default</span>
                    </label>
                    {editingAddressIndex === null && activeAddresses.length === 0 ? (
                      <p className="text-xs text-gray-500">The first address is always the default.</p>
                    ) : null}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={2}
                      value={addressDraft.Address || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, Address: e.target.value }))}
                      className="w-full resize-none border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Location URL</label>
                    <input
                      type="text"
                      value={addressDraft.LocationUrl || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, LocationUrl: e.target.value }))}
                      className="w-full border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">PO Box</label>
                    <input
                      type="text"
                      value={addressDraft.POBOX || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, POBOX: e.target.value }))}
                      className="w-full border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4">
                  {editingAddressIndex !== null && (
                    <button
                      type="button"
                      onClick={cancelAddressEdit}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={commitAddressFromDraft}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#46955A] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#3d7d4f]"
                  >
                    {editingAddressIndex !== null ? (
                      <>
                        <Pencil className="h-4 w-4" />
                        Save changes
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Add to list
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">Address list</h3>
              <p className="mt-1 text-sm text-gray-500">All addresses saved for this customer.</p>
              <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">#</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">Emirate</th>
                        <th className="min-w-[140px] px-4 py-3 font-semibold text-gray-700">Location</th>
                        <th className="min-w-[200px] px-4 py-3 font-semibold text-gray-700">Address</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">PO Box</th>
                        <th className="min-w-[120px] px-4 py-3 font-semibold text-gray-700">Location URL</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">Default</th>
                        <th className="whitespace-nowrap px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {activeAddresses.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                            No addresses yet. Use the form above to add the first address.
                          </td>
                        </tr>
                      ) : (
                        activeAddresses.map((address, displayIndex) => {
                          const globalIndex = customer.CustomerAddresses.findIndex((a) => a === address);
                          return (
                            <tr
                              key={`${globalIndex}-${displayIndex}`}
                              className={`align-top hover:bg-gray-50/80 ${
                                editingAddressIndex === globalIndex ? 'bg-[#f0f9f4]/90' : ''
                              }`}
                            >
                              <td className="whitespace-nowrap px-4 py-3 text-gray-600">{displayIndex + 1}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-900">{getEmirateName(address.EmirateId)}</td>
                              <td className="px-4 py-3 text-gray-900">{address.Location?.Name || '—'}</td>
                              <td className="max-w-xs px-4 py-3 text-gray-700">
                                <span className="line-clamp-3 whitespace-pre-wrap">{address.Address || '—'}</span>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.POBOX || '—'}</td>
                              <td className="max-w-[180px] truncate px-4 py-3 text-gray-700" title={address.LocationUrl || ''}>
                                {address.LocationUrl || '—'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                {address.IsDefault ? (
                                  <span className="inline-flex rounded-full bg-[#f0f9f4] px-2.5 py-0.5 text-xs font-medium text-[#46955A]">
                                    Default
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && setActiveAddressDefault(globalIndex)}
                                    className="text-xs font-medium text-[#46955A] hover:underline"
                                  >
                                    Set default
                                  </button>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right">
                                <div className="inline-flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && startEditAddress(globalIndex)}
                                    className="inline-flex items-center justify-center rounded-lg p-2 text-[#46955A] transition-colors hover:bg-[#f0f9f4]"
                                    title="Edit address"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && removeAddress(globalIndex)}
                                    className="inline-flex items-center justify-center rounded-lg bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100"
                                    title="Remove address"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddCustomer;
